import { useCallback, useEffect, useState } from 'react';
import { useAuth, useLocale } from '../../context';
import { api } from '../../api';
import {
  createPlatformCredential,
  isPlatformAuthenticatorAvailable,
  markLocalPasskeyHint,
  clearLocalPasskeyHint,
  mapWebAuthnError,
} from '../../utils/webauthn';

export default function PasskeyManager({ compact = false }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const { token } = useAuth();
  const [supported, setSupported] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.webauthnCredentials(token);
      const list = res.credentials || [];
      setCredentials(list);
      if (list.length) markLocalPasskeyHint();
      else clearLocalPasskeyHint();
    } catch (err) {
      setError(err.message || tr('auth.webauthn.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [token, tr]);

  useEffect(() => {
    let cancelled = false;
    isPlatformAuthenticatorAvailable()
      .then((ok) => { if (!cancelled) setSupported(ok); })
      .catch(() => { if (!cancelled) setSupported(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const register = async () => {
    setError('');
    setMsg('');
    setBusy(true);
    try {
      const options = await api.webauthnRegisterOptions(token);
      const { challengeId, ...publicKey } = options;
      const attestation = await createPlatformCredential(publicKey);
      if (!attestation) throw new Error(tr('auth.webauthn.registerFailed'));
      await api.webauthnRegisterVerify(token, {
        challengeId,
        response: attestation,
        deviceName: tr('auth.webauthn.thisDevice'),
      });
      markLocalPasskeyHint();
      setMsg(tr('auth.webauthn.registered'));
      await load();
    } catch (err) {
      const code = mapWebAuthnError(err);
      if (code === 'cancelled') setError('');
      else if (code === 'already_registered') setError(tr('auth.webauthn.alreadyRegistered'));
      else if (code === 'unsupported') setError(tr('auth.webauthn.unsupported'));
      else setError(err.message || tr('auth.webauthn.registerFailed'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setError('');
    setMsg('');
    setBusy(true);
    try {
      await api.webauthnDeleteCredential(token, id);
      setMsg(tr('auth.webauthn.removed'));
      await load();
    } catch (err) {
      setError(err.message || tr('auth.webauthn.removeFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`passkey-manager${compact ? ' passkey-manager--compact' : ''}`}>
      {!compact ? <h3 className="passkey-manager__title">{tr('auth.webauthn.manageTitle')}</h3> : null}
      <p className="passkey-manager__desc">{tr('auth.webauthn.manageDesc')}</p>
      {loading ? <div className="passkey-manager__loading">...</div> : null}
      {!loading && !credentials.length ? (
        <div className="passkey-manager__empty">{tr('auth.webauthn.empty')}</div>
      ) : null}
      <ul className="passkey-manager__list">
        {credentials.map((item) => (
          <li key={item.id} className="passkey-manager__item">
            <div className="passkey-manager__meta">
              <strong>{item.deviceName}</strong>
              <span>
                {item.createdAt
                  ? new Date(item.createdAt).toLocaleString()
                  : ''}
              </span>
            </div>
            <button
              type="button"
              className="passkey-manager__remove"
              disabled={busy}
              onClick={() => remove(item.id)}
            >
              {tr('auth.webauthn.remove')}
            </button>
          </li>
        ))}
      </ul>
      {supported ? (
        <button type="button" className="account-submit" onClick={register} disabled={busy}>
          {busy ? '...' : tr('auth.webauthn.add')}
        </button>
      ) : (
        <div className="passkey-manager__hint">{tr('auth.webauthn.unsupported')}</div>
      )}
      {error ? <div className="account-error">{error}</div> : null}
      {msg ? <div className="account-success">{msg}</div> : null}
    </div>
  );
}
